// @flow

import React, { useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import actionCreators from "@modules/service/actionCreator";
import { View, Button } from "@components";
import { getRouteClientIdParam, getRouteParam } from "@selectors";
import type { Rtw } from "@modules/service/reducer/rtw/types";
import type { ApprovableContainer } from "@modules/service/types";
import {
  RtwSettingsView,
  RtwFormView,
  RtwNotificationAttachmentsForm,
  RtwNotificationsView,
  RtwComplianceView,
  RtwReasonForLateSubmission,
  RtwReasonScopeView,
  CommonComplianceKpisView,
  RtwEditModal,
} from "@modules/service/components";

type OwnProps = $Exact<{
  clientId: ?number,
  data: ApprovableContainer<Rtw>,
  schema: Object,
  processId?: number,
}>;

type PropsFromDispatch = $Exact<{
  setRtwDraft: typeof actionCreators.SetRtwDraft,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

const RtwDetailsView = props => {
  const lsr = props.data.content.lateSubmissionReasons;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState(1);

  const toggleModal = () => {
    setIsModalOpen(prev => !prev);
  };

  const handleEditStep = (step: number) => () => {
    setModalStep(step);
    props.setRtwDraft(props.data);
    toggleModal();
  };

  const getSchema = () => {
    return {
      kpis: ((((props || {}).schema || {}).properties || {}).kpis || {}).items,
    };
  };

  return (
    <>
      {/* Settings */}
      <View className="gs-container">
        <View direction="row" alignItems="center" justifyContent="space-between">
          <h6>{props.t("RETURN_TO_WORK_DETAILS_SETTINGS")}</h6>
          <Button materialIcon="edit" onClick={handleEditStep(1)}>
            {props.t("EDIT")}
          </Button>
        </View>
        <RtwSettingsView data={props.data.content} processId={props.processId} />
        <RtwReasonScopeView data={props.data.content} schema={props.schema} />
      </View>
      {/* Compliance */}
      <View className="gs-container" gap={6}>
        <View direction="row" alignItems="center" justifyContent="space-between">
          <h6>{props.t("RETURN_TO_WORK_DETAILS_COMPLIANCE")}</h6>
          <Button materialIcon="edit" onClick={handleEditStep(2)}>
            {props.t("EDIT")}
          </Button>
        </View>
        <RtwComplianceView data={props.data.content} />

        <View className="gs-container">
          <CommonComplianceKpisView
            data={props.data.content.kpis}
            schema={getSchema().kpis || {}}
          />
        </View>

        {(lsr.length && (
          <View className="gs-container">
            <RtwReasonForLateSubmission
              schema={props.schema.properties.lateSubmissionReasons.items}
              data={lsr}
              disabled
            />
          </View>
        )) || <div />}
      </View>
      {/* Form */}
      <View className="gs-container" gap={6}>
        <View direction="row" alignItems="center" justifyContent="space-between">
          <h6>{props.t("RETURN_TO_WORK_DETAILS_FORM")}</h6>
          <Button materialIcon="edit" onClick={handleEditStep(3)}>
            {props.t("EDIT")}
          </Button>
        </View>
        <RtwFormView data={props.data.content} schema={props.schema} />
      </View>
      <View className="gs-container" gap={6}>
        <View direction="row" justifyContent="space-between">
          <h6>{props.t("RETURN_TO_WORK_DETAILS_NOTIFICATIONS")}</h6>
          <Button materialIcon="edit" onClick={handleEditStep(4)}>
            {props.t("EDIT")}
          </Button>
        </View>
        <RtwNotificationsView data={props.data.content} />
        <View className="gs-container">
          <RtwNotificationAttachmentsForm
            clientId={props.clientId}
            processId={props.processId}
            data={props.data.content.attachments}
            schema={props.schema}
          />
        </View>
      </View>
      {isModalOpen && (
        <RtwEditModal
          clientId={props.clientId}
          editing={isModalOpen}
          showModal={isModalOpen}
          processId={props.processId}
          onCancel={toggleModal}
          defaultStep={modalStep}
        />
      )}
    </>
  );
};

const mapDispatchToProps: PropsFromDispatch = {
  setRtwDraft: actionCreators.SetRtwDraft,
};

export default compose(localize(), connect(null, mapDispatchToProps))(RtwDetailsView);
